// frontend/src/pages/GrievancesPage.js
//
// Member side of grievances (migration_048). Any active playing member can
// raise a grievance or complaint, with his name shown or anonymously. Only the
// super admin can see it. The member sees his own grievances, the replies, and
// can add a follow-up.

import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';

const NAVY = '#1E3A6E';
const STATUS = {
  open:        { label: 'Open',        bg: '#fef3c7', fg: '#92400e' },
  in_progress: { label: 'In progress', bg: '#dbeafe', fg: '#1d4ed8' },
  resolved:    { label: 'Resolved',    bg: '#dcfce7', fg: '#166534' },
  closed:      { label: 'Closed',      bg: '#f1f5f9', fg: '#475569' },
};
const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function NewGrievance({ categories, onDone }) {
  const [category, setCategory] = useState('other');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit() {
    setErr('');
    if (subject.trim().length < 3) return setErr('Please write a short subject.');
    if (body.trim().length < 10) return setErr('Please describe the grievance (at least a sentence).');
    if (!window.confirm(anonymous
      ? 'Post ANONYMOUSLY?\n\nYour name will not be shown to anyone, and is not stored with this grievance.'
      : 'Post with your NAME shown?')) return;
    setBusy(true);
    try {
      await api.post('/grievances', { category, subject: subject.trim(), body: body.trim(), anonymous });
      setSubject(''); setBody(''); setAnonymous(false); setCategory('other');
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not post. Please try again.');
    } finally { setBusy(false); }
  }

  const choice = (on) => ({ flex: 1, padding: '10px 8px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
    border: `1.5px solid ${on ? NAVY : '#e5e7eb'}`, background: on ? '#eef2ff' : 'white', fontWeight: 700, fontSize: '0.82rem', color: on ? NAVY : '#475569' });

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 18 }}>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem', color: NAVY, marginBottom: 10 }}>Raise a grievance</div>

      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>Category</label>
      <select className="form-control" value={category} onChange={e => setCategory(e.target.value)} style={{ marginBottom: 10 }}>
        {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>

      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>Subject</label>
      <input className="form-control" maxLength={150} value={subject} onChange={e => setSubject(e.target.value)}
        placeholder="In a few words" style={{ marginBottom: 10 }} />

      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>Details</label>
      <textarea className="form-control" rows={5} maxLength={5000} value={body} onChange={e => setBody(e.target.value)}
        placeholder="What happened, when, and what you would like done" style={{ marginBottom: 12, resize: 'vertical' }} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <div style={choice(!anonymous)} onClick={() => setAnonymous(false)}>🙋 Show my name</div>
        <div style={choice(anonymous)} onClick={() => setAnonymous(true)}>🕶️ Post anonymously</div>
      </div>
      <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 12 }}>
        {anonymous
          ? 'Your name is not shown to anyone and is not stored with the grievance. You will still see the replies here.'
          : 'Your name will be shown with the grievance.'}
      </div>

      {err && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 8 }}>{err}</div>}
      <button onClick={submit} disabled={busy}
        style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: NAVY, color: 'white',
          fontWeight: 800, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.95rem', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Posting…' : 'Post grievance'}
      </button>
    </div>
  );
}

function MyGrievance({ g, categories, onChange }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const st = STATUS[g.status] || STATUS.open;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && g.new_reply) api.post(`/grievances/${g.id}/seen`).then(onChange).catch(() => {});
  }
  async function send() {
    if (msg.trim().length < 2) return;
    setBusy(true);
    try { await api.post(`/grievances/${g.id}/messages`, { body: msg.trim() }); setMsg(''); onChange(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not send'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ background: 'white', border: `1px solid ${g.new_reply ? '#93c5fd' : '#e5e7eb'}`, borderRadius: 10, marginBottom: 8 }}>
      <div onClick={toggle} style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: NAVY }}>
            {g.new_reply && <span style={{ color: '#2563eb' }}>● </span>}{g.subject}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
            {categories[g.category] || g.category} · {when(g.created_at)} · {g.is_anonymous ? '🕶️ anonymous' : '🙋 named'}
            {g.new_reply ? ' · new reply' : ''}
          </div>
        </div>
        <span style={{ background: st.bg, color: st.fg, borderRadius: 6, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 800 }}>{st.label}</span>
      </div>
      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          <div style={{ fontSize: '0.85rem', color: '#334155', whiteSpace: 'pre-wrap', background: '#f8fafc', borderRadius: 8, padding: 10 }}>{g.body}</div>
          {g.messages.map(m => (
            <div key={m.id} style={{ marginTop: 8, display: 'flex', justifyContent: m.from_admin ? 'flex-start' : 'flex-end' }}>
              <div style={{ maxWidth: '85%', background: m.from_admin ? '#eef2ff' : '#f1f5f9', borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, color: m.from_admin ? NAVY : '#64748b' }}>
                  {m.from_admin ? 'Management' : 'You'} · {when(m.created_at)}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#334155', whiteSpace: 'pre-wrap' }}>{m.body}</div>
              </div>
            </div>
          ))}
          {g.status !== 'closed' ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <input className="form-control" value={msg} onChange={e => setMsg(e.target.value)} placeholder="Add a follow-up…" />
              <button onClick={send} disabled={busy || msg.trim().length < 2}
                style={{ padding: '0 14px', borderRadius: 8, border: 'none', background: NAVY, color: 'white', fontWeight: 700, cursor: 'pointer' }}>Send</button>
            </div>
          ) : (
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 10 }}>This grievance is closed. Post a new one if needed.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GrievancesPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    api.get('/grievances/mine').then(r => setData(r.data)).catch(e => setErr(e.response?.data?.error || 'Could not load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ paddingBottom: 30 }}>
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '16px 16px 14px' }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Club</div>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.35rem', color: NAVY }}>📮 Grievances</div>
        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
          Raise a grievance or complaint with the management — with your name, or anonymously.
        </div>
      </div>
      <div style={{ padding: 16 }}>
        {err && <div style={{ color: '#dc2626' }}>{err}</div>}
        {!err && !data && <div className="loading-spinner"><div className="spinner" /></div>}
        {data && (
          <>
            <NewGrievance categories={data.categories} onDone={load} />
            <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>
              My grievances ({data.grievances.length})
            </div>
            {data.grievances.length === 0 && <div style={{ fontSize: '0.85rem', color: '#64748b' }}>You have not raised any grievance.</div>}
            {data.grievances.map(g => <MyGrievance key={g.id} g={g} categories={data.categories} onChange={load} />)}
          </>
        )}
      </div>
    </div>
  );
}
